import { useState } from "react";
import { dayKey, todayKey } from "../lib/date";

interface Props {
  /** Nombre de tâches à échéance par jour (clé YYYY-MM-DD). */
  dueCount: Record<string, number>;
  selected: string[];
  onSelect: (days: string[]) => void;
  weekStart: "mon" | "sun";
}

const DOW_MON = ["L", "M", "M", "J", "V", "S", "D"];
const DOW_SUN = ["D", "L", "M", "M", "J", "V", "S"];

/** Mini-calendrier sidebar — multi-sélection de jours = filtre (design DECO). */
export function MiniCalendar({
  dueCount,
  selected,
  onSelect,
  weekStart,
}: Props) {
  const [cursor, setCursor] = useState(0);
  const today = todayKey();

  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth() + cursor, 1);
  const offset = weekStart === "mon" ? (base.getDay() + 6) % 7 : base.getDay();
  const start = new Date(base);
  start.setDate(1 - offset);

  const cells: { key: string; day: number; inMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push({
      key: dayKey(d),
      day: d.getDate(),
      inMonth: d.getMonth() === base.getMonth(),
    });
  }

  const toggle = (key: string) =>
    onSelect(
      selected.includes(key)
        ? selected.filter((k) => k !== key)
        : [...selected, key],
    );

  return (
    <div style={{ padding: "8px 14px 12px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          fontSize: 11.5,
          fontWeight: 600,
          color: "var(--ink-2)",
          marginBottom: 6,
        }}
      >
        <span style={{ textTransform: "capitalize" }}>
          {base.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
        </span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 2 }}>
          <button
            className="sidebar-collapse"
            onClick={() => setCursor((c) => c - 1)}
            aria-label="Mois précédent"
          >
            ‹
          </button>
          <button
            className="sidebar-collapse"
            onClick={() => setCursor(0)}
            aria-label="Mois courant"
          >
            ·
          </button>
          <button
            className="sidebar-collapse"
            onClick={() => setCursor((c) => c + 1)}
            aria-label="Mois suivant"
          >
            ›
          </button>
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7,1fr)",
          gap: 1,
        }}
      >
        {(weekStart === "mon" ? DOW_MON : DOW_SUN).map((d, i) => (
          <span
            key={i}
            style={{
              fontSize: 9,
              color: "var(--ink-ghost)",
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            {d}
          </span>
        ))}
        {cells.map((c) => {
          const sel = selected.includes(c.key);
          const isToday = c.key === today;
          return (
            <button
              key={c.key}
              onClick={() => toggle(c.key)}
              style={{
                fontSize: 10,
                lineHeight: "18px",
                textAlign: "center",
                borderRadius: 4,
                cursor: "pointer",
                color: sel
                  ? "#fff"
                  : c.inMonth
                    ? "var(--ink-2)"
                    : "var(--ink-disabled)",
                background: sel
                  ? "var(--accent)"
                  : isToday
                    ? "var(--accent-soft)"
                    : "transparent",
                fontWeight: isToday || sel ? 700 : 400,
                position: "relative",
              }}
            >
              {c.day}
              {dueCount[c.key] ? (
                <span
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 3,
                    height: 3,
                    borderRadius: "50%",
                    background: sel ? "#fff" : "var(--accent)",
                  }}
                />
              ) : null}
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <button
          onClick={() => onSelect([])}
          style={{
            fontSize: 10.5,
            color: "var(--accent)",
            marginTop: 6,
            cursor: "pointer",
          }}
        >
          Effacer le filtre ({selected.length} j.)
        </button>
      )}
    </div>
  );
}
